/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment } from "react"
import { actionCreator } from "store/store"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import {
  MultiItemSelectionDrawer,
  MultiItemSelectionDrawerV3,
} from "components/drawers"
import { useMenuItemSearch } from "hooks/useMenuItemSearch"
import { isItemWithoutPhotoMode } from "utils/menu_helper"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { MenuOverviewHeader } from "./MenuOverviewHeader"
import { MenuOverviewTable } from "./MenuOverviewTable"
import { MenuOverviewItemsResult } from "./lists"

interface IProps {
  hasAction?: boolean
  routePrefix?: string
}

export const MenuOverviewContent: FC<IProps> = ({
  hasAction = true,
  routePrefix,
}: IProps) => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    drawer: { showItemSelection },
    overview: { sellingTime },
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  const { hasActiveSearch } = useMenuItemSearch()

  const isFilterActive = isItemWithoutPhotoMode(itemFilter) || !!sellingTime

  return (
    <Fragment>
      <div
        css={css`
          height: ${showItemSelection ? "calc(100% - 90px)" : "100%"};
          display: flex;
          flex-flow: column;
        `}
      >
        <div
          css={css`
            margin-bottom: 20px;
          `}
        >
          <MenuOverviewHeader />
        </div>
        {hasActiveSearch ? (
          <MenuOverviewItemsResult
            hasAction={hasAction}
            routePrefix={routePrefix}
          />
        ) : (
          <MenuOverviewTable hasAction={hasAction} routePrefix={routePrefix} />
        )}
      </div>
      {showItemSelection && enablePartialMenu && !isFilterActive && (
        <MultiItemSelectionDrawerV3
          data-testid="multi-item-selection-drawer-v3"
          visible={showItemSelection}
          onClose={() => {
            dispatch(actionCreator.bulkSelectionsV3.reset())
            dispatch(
              actionCreator.menu.toggleDrawer({
                showItemSelection: false,
              }),
            )
          }}
        />
      )}
      {showItemSelection && (!enablePartialMenu || isFilterActive) && (
        <MultiItemSelectionDrawer
          data-testid="multi-item-selection-drawer"
          visible={showItemSelection}
          onClose={() => {
            dispatch(
              actionCreator.menu.toggleDrawer({
                showItemSelection: false,
                itemSelections: {},
              }),
            )
          }}
        />
      )}
    </Fragment>
  )
}
